import React, { useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import InputForm from "src/components/ComponentesFormularios/InputForm";
import { optionsEstadosSelect, optionsYesNoResponse } from "src/data/ConstantForm";
import Tooltip from "@mui/material/Tooltip";
import { Colors } from "src/assets/colors/Colors";
import { useDispatch } from "react-redux";
import { GlobalConstants } from "src/data/GlobalConstants";
import SelectFormVirtualize from "src/components/ComponentesFormularios/SelectFormVirtualize";
import { modalCRUDService } from "src/services/modalCRUDService";
import { dateToStringFormat, getCurrentTime, getFechaToCalendarRange, toBlob } from "src/util/functions";
import { CalendarFormRange } from "src/components/ComponentesFormularios/CalendarForm";
import ImagePickerCrop from "src/components/ImagePickerCrop";
import { popUpsService } from "src/services/unoauno/apiPopUps";

const FormCRUDPopUps = ({properties}) => {
    const dispatch = useDispatch();

    const { data: { 
        proveedor,
        dataPop,
        segmentaciones,
        productos,
        edit,
        reload
    } } = properties;
    
    const methods = useForm({ defaultValues: {
        nombre: edit ? dataPop.nombre : '',
        fecha: edit ? getFechaToCalendarRange(dataPop.fechaInicio, dataPop.fechaFin) : [new Date(), new Date()],
        productosSelected: edit ? dataPop.productos?.split(",") ?? [] : [],
        asignaciones: edit ? dataPop.asignaciones : [],
        estado: edit ? optionsYesNoResponse.find(el => el.id == dataPop.estado) : optionsEstadosSelect[0],
        evento: edit ? optionsYesNoResponse.find(el => el.id == dataPop.evento) : optionsYesNoResponse[0]
    }});
    const { control, setValue, handleSubmit, getValues, formState: { errors } } = methods;

    const [urlImagen, setUrlImagen] = useState(edit ? dataPop?.urlImagen ?? "" : "");
    const previewRef = useRef(null);

    const productsSelected = useWatch({ control, name: 'productosSelected' });
    const asignaciones = useWatch({ control, name: 'asignaciones' });


    const onClickGuardar = async (data) => {
        let idPopUp = dataPop.id;
        
        if(asignaciones.length == 0)  return dispatch({ type: 'setStateAlert', alert: { type: GlobalConstants.alertWarning , message: 'Debes agregar al menos una segmentación' } });
        
        if(urlImagen == '' && !previewRef.current) return dispatch({ type: 'setStateAlert', alert: { type: GlobalConstants.alertWarning , message: 'Debes agregar una imagen' } });
        
        try {
            if(edit) {
                await popUpsService.updatePopUp({
                    id: dataPop.id,
                    nombre: data.nombre,
                    productos: productsSelected?.length > 0 ? productsSelected.join(",") : null,
                    fechaInicio: dateToStringFormat(data.fecha[0]),
                    fechaFin: dateToStringFormat(data.fecha[1]),
                    evento: data.evento.id,
                    estado: data.estado.id,
                    asignaciones: data.asignaciones
                });
            }else {
                const response = await popUpsService.savePopUp({
                    nombre: data.nombre,
                    proveedor: proveedor,
                    productos: productsSelected?.length > 0 ? productsSelected.join(",") : null,
                    fechaInicio: dateToStringFormat(data.fecha[0]),
                    fechaFin: dateToStringFormat(data.fecha[1]),
                    evento: data.evento.id,
                    estado: data.estado.id,
                    asignaciones: data.asignaciones
                });

                idPopUp = response.data;
            }
            
            if(previewRef?.current) {
                const nombreImg = `pop_${idPopUp}_${getCurrentTime()}.png`
                
                const blobImg = await toBlob(previewRef.current);
                const file = new File([blobImg], nombreImg);

                await popUpsService.saveImagen({file, idPopUp, nombreImg, extension: "/popUps"})
            }

            reload();
            modalCRUDService.clear();

            dispatch({ type: 'setStateAlert', alert: { type: GlobalConstants.alertSuccess , message: 'Solicitud exitosa' } });
        } catch (error) {
            console.log(error);
            dispatch({ type: 'setStateAlert', alert: { type: GlobalConstants.alertError , message: 'Ha ocurrido un error, comunicate con soporte' } });
        }
    }

    const addProduct = () => {
        const product = getValues('producto');
        if(!product) return;

        if(!productsSelected.some(pro => pro == product.id)) {
            setValue('productosSelected', [...productsSelected, product.id]);
            setValue('producto', null);    
        }else {
            dispatch({ type: 'setStateAlert', alert: { type: GlobalConstants.alertWarning , message: 'El producto ya se encuentra asignado' } });
        }
    }

    const deleteProduct = (id) => setValue('productosSelected', productsSelected.filter(asg => asg != id));

    const addAsignacion = () => {
        const segmentacion = getValues('segmentacion');
        if(!segmentacion) return;

        if(!asignaciones.some(asg => asg == segmentacion.id)) {
            setValue('asignaciones', [...asignaciones, segmentacion.id]);
            setValue('segmentacion', null);
        }else {
            dispatch({ type: 'setStateAlert', alert: { type: GlobalConstants.alertWarning , message: 'La segmentación ya se encuentra asignada' } });
        }
    }

    const deleteAsignacion = (id) => setValue('asignaciones', asignaciones.filter(asg => asg != id));

    const getNameSegmentaciones = (id) => {
        const segmentacion = segmentaciones.find(seg => seg.id == id);
        return "Segmentación_" + segmentacion?.text ?? '';
    }

    return (
        <FormProvider {...methods}>
            <div className="p-2 w-100">
                <Tooltip title="Guardar" aria-label="guardar" style={{position: "absolute", right: "3.5%", zIndex: 1}}>
                    <a onClick={handleSubmit(onClickGuardar, () => {})} className="icon-container-actions d-flex">
                        <div style={{backgroundColor: Colors.COLOR_GREEN_DARK}}/>
                        <i style={{color: Colors.COLOR_GREEN_DARK}} className="fas fa-save fa-2x"/>
                    </a>
                </Tooltip>
            
                <div className="w-100 row m-0 px-2">
                    <InputForm
                        inputProps={{ control }}
                        identificador="nombre" 
                        label="Nombre"
                        placeholder=""
                        required={true}
                        textUppercase={false}
                        className="px-2 col-3"
                        customError={errors.nombre ? "El campo no puede estar vacío" : null}
                    />
                    
                    <CalendarFormRange
                        inputProps={{ control }}
                        disabled={false}
                        identificador="fecha" 
                        label="Fecha de activación"
                        required={true}
                        className="w-auto mb-2"
                        customError={errors.fecha ? "El campo no puede estar vacío" : null}
                    />
                </div>

                <div className="w-100 row m-0 px-2 mt-1">
                    <SelectFormVirtualize
                        isMulti={false}
                        inputProps={{ control }}
                        disabled={false}
                        showId={false}
                        identificador="estado"
                        label="Estado"
                        options={optionsEstadosSelect}
                        required={true}
                        className="px-2 col-2"
                        customError={errors.estado ? "El campo no puede estar vacío" : null}
                    />

                    <SelectFormVirtualize
                        isMulti={false}
                        inputProps={{ control }}
                        disabled={false}
                        showId={false}
                        identificador="evento"
                        label="Es de Evento"
                        options={optionsYesNoResponse}
                        required={true}
                        className="px-2 col-2"
                        customError={errors.evento ? "El campo no puede estar vacío" : null}
                    />
                </div>

                <div className="px-3 mt-3 pt-1">
                    <div className="pt-2 px-2" style={{borderTop: "0.5px solid #D2D2D2"}}>
                        <strong style={{fontSize: "14pt"}}>Productos asignados:</strong>
                        <div className="d-flex position-relative align-items-center mt-3">
                            <SelectFormVirtualize
                                isMulti={false}
                                inputProps={{ control }}
                                disabled={false}
                                showId={true}
                                identificador="producto"
                                label="Productos"
                                options={productos}
                                required={false}
                                className="px-2 col-4"
                            />

                            <Tooltip title="Agregar" aria-label="agregar" style={{marginLeft: "10px"}}>
                                <a onClick={addProduct} className="icon-container-actions d-flex">
                                    <div style={{backgroundColor: Colors.COLOR_GREEN_DARK}}/>
                                    <i style={{color: Colors.COLOR_GREEN_DARK}} className="fas fa-plus fa-2x"/>
                                </a>
                            </Tooltip>
                        </div>

                        <div className="w-100 px-2 mt-2">
                            <strong>Productos asignados:</strong>

                            <div className="w-100 row m-0 p-0">
                                {productsSelected.length > 0 ? 
                                    productsSelected.map(( pro, index ) => 
                                        <div key={index} className="containerTag">
                                            <span>{pro}</span>
                                            <i className="fas fa-times" onClick={() => deleteProduct(pro)}/>
                                        </div>
                                    ): 
                                    <div className="px-2 mt-1">
                                        <p>No hay productos asignados</p>    
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-3 mt-4 pt-1">
                    <div className="pt-2 px-2" style={{borderTop: "0.5px solid #D2D2D2"}}>
                        <strong style={{fontSize: "14pt"}}>Segmentaciones Asignadas:</strong>

                        <div className="d-flex align-items-center mt-3">
                            <SelectFormVirtualize
                                isMulti={false}
                                inputProps={{ control }}
                                disabled={false}
                                showId={false}
                                identificador="segmentacion"
                                label="Segmentaciones"
                                options={segmentaciones}
                                required={false}
                                className="px-2 col-4"
                            />

                            <Tooltip title="Agregar" aria-label="agregar" style={{marginLeft: "10px"}}>
                                <a onClick={addAsignacion} className="icon-container-actions d-flex">
                                    <div style={{backgroundColor: Colors.COLOR_GREEN_DARK}}/>
                                    <i style={{color: Colors.COLOR_GREEN_DARK}} className="fas fa-plus fa-2x"/>
                                </a>
                            </Tooltip>
                        </div>

                        <div className="p-2">
                            {asignaciones.length > 0 ? 
                                asignaciones.map(( asignacion, index ) => 
                                    <div key={index} className="w-100 row m-0 p-0 pl-4 d-flex align-items-center" style={{height: "40px"}}>
                                        <span className="d-block mb-1" key={index}>
                                            <strong style={{fontSize: "16px", fontWeight: "bold", marginBottom: "5px"}}>»</strong>&nbsp;&nbsp;{getNameSegmentaciones(asignacion)}
                                        </span>

                                        <Tooltip title="Eliminar" aria-label="eliminar" style={{marginLeft: "20px"}}>
                                            <a onClick={() => deleteAsignacion(asignacion)} className="icon-container-actions d-flex">
                                                <div style={{backgroundColor: Colors.ROJO_PASTEL}}/>
                                                <i style={{color: Colors.ROJO_PASTEL}} className="fas fa-trash fa-2x"/>
                                            </a>
                                        </Tooltip>
                                    </div>
                                )
                                : 
                                <div className="ml-5">
                                    <p>No hay segmentaciones asignadas</p>
                                </div>
                            }
                        </div>
                    </div>
                </div>

                <div className="mt-3 px-4">
                    <strong style={{fontSize: "14pt"}}>Imagen asignada:</strong>
                    <div className="mt-2">
                        <ImagePickerCrop previewRef={previewRef} urlImagen={urlImagen} ratio={0.5}/>
                    </div>
                </div>
            </div>
        </FormProvider>
    )
}

export default FormCRUDPopUps